/**
 * The numeric keypad. A first-class screen element, not a fallback.
 *
 * The OS keyboard is the wrong tool at a counter: it takes 300 ms to animate in, covers the
 * bottom half of the screen exactly where the total is, and on Android offers a full QWERTY with
 * a number row the user has to hunt for. This is nine large targets that never move.
 */

import { cn } from './cn.js';

export type KeypadAction = 'backspace' | 'clear';

export type KeypadProps = {
  onDigit: (digit: string) => void;
  onAction: (action: KeypadAction) => void;
  /** Replaces the bottom-left key. `00` for money, nothing for a PIN. */
  extraKey?: '00' | '.' | null;
  disabled?: boolean;
  /**
   * Comfortable-height keys at every screen size, for a sheet that carries more above the pad
   * than a display and a title — the change sheet's note buttons. Still well above 48px.
   */
  compact?: boolean;
  className?: string;
};

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

export function Keypad({ onDigit, onAction, extraKey = null, disabled = false, compact = false, className }: KeypadProps) {
  return (
    <div
      className={cn('grid grid-cols-3 gap-2.5', className)}
      role="group"
      aria-label="Number pad"
    >
      {DIGITS.map((digit) => (
        <Key key={digit} label={digit} compact={compact} disabled={disabled} onPress={() => { onDigit(digit); }} />
      ))}

      {extraKey === null ? (
        <Key
          label="Clear"
          variant="muted"
          compact={compact}
          disabled={disabled}
          onPress={() => { onAction('clear'); }}
        />
      ) : (
        <Key label={extraKey} compact={compact} disabled={disabled} onPress={() => { onDigit(extraKey); }} />
      )}

      <Key label="0" compact={compact} disabled={disabled} onPress={() => { onDigit('0'); }} />

      <Key
        label="Backspace"
        variant="muted"
        compact={compact}
        disabled={disabled}
        onPress={() => { onAction('backspace'); }}
      >
        <BackspaceIcon />
      </Key>
    </div>
  );
}

type KeyProps = {
  label: string;
  onPress: () => void;
  disabled: boolean;
  variant?: 'digit' | 'muted';
  compact: boolean;
  children?: React.ReactNode;
};

function Key({ label, onPress, disabled, variant = 'digit', compact, children }: KeyProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPress}
      aria-label={label}
      className={cn(
        // Four rows of full-height keys, plus the sheet's title, display and footer, need about
        // 810px of screen; a sheet may take 85% of it. On a shorter phone the bottom row — the 0 —
        // fell below the fold and had to be scrolled to, mid-sale. There the keys drop to the
        // comfortable target, still well above the 48px floor. px, not rem: a media query ignores
        // the till's 17px root.
        'flex items-center justify-center rounded-lg font-semibold tabular-nums',
        compact ? 'h-comfortable' : 'h-keypad [@media(max-height:820px)]:h-comfortable',
        'transition-[background-color,transform] duration-instant ease-springSubtle',
        'active:scale-press disabled:pointer-events-none disabled:opacity-40',
        variant === 'digit'
          ? 'bg-surface-raised text-content-primary text-2xl shadow-sm active:bg-action-subtle'
          : 'bg-transparent text-content-secondary text-base active:bg-surface-sunken',
      )}
    >
      {children ?? (variant === 'digit' ? label : <span className="text-sm">{label}</span>)}
    </button>
  );
}

function BackspaceIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 5h11a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H9L3 12l6-7Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path d="m12 9.5 5 5m0-5-5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
